/**
 * COMPTABILITÉ — CE QUE DIT UNE REDDITION SHOTGUN, SUR SA FICHE.
 *
 * Une reddition est un CONTRÔLE, jamais une caisse : sa lecture ne produit
 * aucune ligne source. Sa fiche n'affichait donc qu'un « la lecture n'a extrait
 * aucune ligne » — vrai pour un rapport ZAP raté, trompeur ici (recette du
 * 08/10/2026). Ce bloc redit les chiffres lus, rangés comme sur le PDF :
 *
 *   · ce que l'organisateur a facturé : en ligne, sur place, total ;
 *   · ce que Shotgun lui a facturé, LIGNE À LIGNE, dont la somme fait le
 *     total contrôlé par le lecteur : commissions, remises commerciales,
 *     frais de surclassement de billet (30 % de la différence de tarif quand
 *     un billet revendu passe dans une catégorie supérieure) ;
 *   · le solde reversé, et les billets.
 *
 * Un montant non lu s'écrit « — ». Une lecture antérieure au lecteur v2 ne
 * connaissait pas la ligne de surclassement : on le dit, sans l'inventer.
 */
import type { ReactNode } from 'react'
import { formatMoney } from './money.js'
import { cents, estObjet, texte } from './RevenueShared.js'

export interface MontantTaxeVue { ht: number | null; vat: number | null; ttc: number | null }

export interface RedditionVue {
  reportId: string | null
  ventes: MontantTaxeVue
  enLigne: MontantTaxeVue
  surPlace: MontantTaxeVue
  commissions: MontantTaxeVue | null
  remises: MontantTaxeVue | null
  surclassement: MontantTaxeVue | null
  surclassementConnu: boolean
  frais: MontantTaxeVue
  collecteEnLigne: number | null
  solde: number | null
  billets: { emis: number | null; rembourses: number | null; revendus: number | null; finaux: number | null }
}

function montant(v: unknown): MontantTaxeVue | null {
  if (!estObjet(v)) return null
  return { ht: cents(v['ht']), vat: cents(v['vat']), ttc: cents(v['ttc']) }
}
const VIDE: MontantTaxeVue = { ht: null, vat: null, ttc: null }

/** `reddition` de `GET /finance/sources/:id` ; `null` pour tout autre document. */
export function lireReddition(v: unknown): RedditionVue | null {
  if (!estObjet(v)) return null
  const billets = estObjet(v['tickets']) ? v['tickets'] : {}
  return {
    reportId: texte(v['report_id']),
    ventes: montant(v['sales']) ?? VIDE,
    enLigne: montant(v['online']) ?? VIDE,
    surPlace: montant(v['onsite']) ?? VIDE,
    commissions: montant(v['commissions']),
    remises: montant(v['discounts']),
    surclassement: montant(v['upgrades']),
    surclassementConnu: v['upgrades_known'] === true,
    frais: montant(v['fees']) ?? VIDE,
    collecteEnLigne: cents(v['collected_online_ttc_cents']),
    solde: cents(v['balance_cents']),
    billets: {
      emis: cents(billets['issued']), rembourses: cents(billets['refunded']),
      revendus: cents(billets['resold']), finaux: cents(billets['final']),
    },
  }
}

const nombre = (n: number | null): string => (n === null ? '—' : n.toLocaleString('fr-FR'))

function Ligne(props: { libelle: ReactNode; aide?: string; m: MontantTaxeVue; total?: boolean }) {
  return (
    <tr className={props.total ? 'fsr-total' : undefined}>
      <th scope="row">
        <span className="fsr-libelle">{props.libelle}</span>
        {props.aide ? <span className="fsr-aide">{props.aide}</span> : null}
      </th>
      <td className="fi-money">{formatMoney(props.m.ht)}</td>
      <td className="fi-money">{formatMoney(props.m.vat)}</td>
      <td className="fi-money">{formatMoney(props.m.ttc)}</td>
    </tr>
  )
}

function Tableau(props: { titre: string; children: ReactNode }) {
  return (
    <article className="fsr-bloc">
      <h3 className="fsr-bloc__titre">{props.titre}</h3>
      <div className="fsr-wrap">
        <table className="fsr-table">
          <thead>
            <tr>
              <th scope="col"><span className="fsr-sr">Ligne</span></th>
              <th scope="col" className="fi-money">HT</th>
              <th scope="col" className="fi-money">TVA</th>
              <th scope="col" className="fi-money">TTC</th>
            </tr>
          </thead>
          <tbody>{props.children}</tbody>
        </table>
      </div>
    </article>
  )
}

export function RedditionResume(props: { r: RedditionVue; statut: string | null }) {
  const { r } = props
  return (
    <section className="fr-section fsr">
      <div className="fr-section__head">
        <h2 className="fr-section__title">Ce que dit la reddition</h2>
        <span className="fr-section__hint">
          {r.reportId ?? 'Reddition Shotgun'} · un contrôle de la nuit, aucune ligne de caisse
        </span>
      </div>
      <div className="fsr-grille">
        <Tableau titre="Ce que vous avez facturé à vos clients">
          <Ligne libelle="Ventes en ligne" m={r.enLigne} />
          <Ligne libelle="Ventes sur place" aide="Le guichet de l’Accueil" m={r.surPlace} />
          <Ligne libelle="Total ventes" m={r.ventes} total />
        </Tableau>
        <Tableau titre="Ce que Shotgun vous a facturé">
          <Ligne libelle="Commissions Shotgun" m={r.commissions ?? VIDE} />
          {r.remises ? <Ligne libelle="Remises commerciales" m={r.remises} /> : null}
          {r.surclassement ? (
            <Ligne
              libelle="Frais de surclassement de billet"
              aide="30 % de la différence de tarif, quand un billet revendu passe dans une catégorie supérieure"
              m={r.surclassement}
            />
          ) : null}
          <Ligne libelle="Total frais Shotgun" m={r.frais} total />
        </Tableau>
      </div>
      {!r.surclassementConnu ? (
        <p className="fi-reserve fsr-note">
          Cette lecture date d’avant la prise en compte des frais de surclassement de billet : s’il y en avait, ils
          n’apparaissent pas ici. « Relire le document » refait la lecture avec le lecteur actuel.
        </p>
      ) : null}
      <dl className="fsr-pied">
        <div><dt>Collecté en ligne par Shotgun</dt><dd className="fi-money">{formatMoney(r.collecteEnLigne)}</dd></div>
        <div><dt>Frais prélevés</dt><dd className="fi-money">{formatMoney(r.frais.ttc === null ? null : -r.frais.ttc)}</dd></div>
        <div className="fsr-pied__fort"><dt>Solde reversé</dt><dd className="fi-money">{formatMoney(r.solde)}</dd></div>
        <div>
          <dt>Billets émis · revendus · remboursés · finaux</dt>
          <dd>{nombre(r.billets.emis)} · {nombre(r.billets.revendus)} · {nombre(r.billets.rembourses)} · {nombre(r.billets.finaux)}</dd>
        </div>
      </dl>
      {props.statut === 'PARSED' ? (
        <p className="lc-hint fsr-note">
          Contrôlé au centime : ventes HT + TVA = TTC, en ligne + sur place = total, commissions − remises + frais de
          surclassement = frais Shotgun, collecté − frais = solde, somme des tarifs = total.
        </p>
      ) : null}
    </section>
  )
}
